/**
 * Shared utilities for purchases data transformation
 * Ensures consistency between PurchasesPage and PurchasesReports
 */

import {
  formatBrandMoney,
  getBrandLocale,
} from './brandCurrency';

/**
 * Format amount for display (SAR / ر.س on Saudi builds)
 */
export const formatAmount = (amount) => {
  if (!amount || isNaN(amount)) return formatBrandMoney(0);
  return formatBrandMoney(Number(amount));
};

/**
 * Format date for display
 */
export const formatDate = (dateStr, language) => {
  const locale = getBrandLocale(language);
  if (!dateStr) return new Date().toLocaleDateString(locale);
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale);
  } catch {
    return dateStr;
  }
};

/**
 * Get supplier name from supplier object
 */
export const getSupplierName = (supplier) => {
  if (!supplier) return 'مورد غير معروف';
  return supplier.name || 'مورد غير معروف';
};

/**
 * Transform raw purchases data to normalized display format
 * This is the SINGLE SOURCE OF TRUTH for purchases data transformation
 */
export const transformPurchasesData = (rawPurchases, suppliers = [], language) => {
  console.log('🔄 Transforming purchases data:', {
    rawPurchasesCount: rawPurchases?.length || 0,
    suppliersCount: suppliers?.length || 0,
    sampleRawPurchase: rawPurchases?.[0]
  });

  if (!rawPurchases || !Array.isArray(rawPurchases)) {
    console.log('⚠️ No valid purchases data to transform');
    return [];
  }

  const transformedPurchases = rawPurchases.map((purchase) => {
    // Find supplier info
    const supplier = suppliers.find(s => s.id === purchase.supplierId);
    
    // Calculate total amount from items (preferred) or use total
    let amount = 0;
    if (Array.isArray(purchase.items) && purchase.items.length > 0) {
      amount = purchase.items.reduce((sum, item) => {
        const itemTotal = Number(item.total) || (Number(item.quantity || 0) * Number(item.price || 0));
        return sum + itemTotal;
      }, 0);
    } else {
      amount = Number(purchase.total || 0);
    }

    const transformedPurchase = {
      id: purchase.id,
      purchaseNumber: purchase.purchaseNumber || purchase.id,
      supplier: purchase.supplierName || getSupplierName(supplier),
      supplierId: purchase.supplierId,
      supplierName: purchase.supplierName || getSupplierName(supplier),
      date: formatDate(purchase.date || purchase.createdAt, language),
      rawDate: purchase.date || purchase.createdAt,
      amount: formatAmount(amount),
      rawAmount: amount,
      status: purchase.status || 'ordered',
      type: purchase.documentType || purchase.type || 'bon-commande',
      items: purchase.items || [],
      notes: purchase.notes || '',
      deliveryDate: purchase.deliveryDate,
      __raw: purchase, // Keep original for debugging
    };

    return transformedPurchase;
  }).sort((a, b) => {
    // Sort by date descending (newest first)
    const dateA = new Date(a.rawDate || Date.now());
    const dateB = new Date(b.rawDate || Date.now());
    return dateB - dateA;
  });

  console.log('✅ Purchases data transformed:', {
    transformedCount: transformedPurchases.length,
    sampleTransformed: transformedPurchases[0],
    totalAmount: transformedPurchases.reduce((sum, purchase) => sum + purchase.rawAmount, 0)
  });

  return transformedPurchases;
};

/**
 * Calculate purchases summary statistics
 */
export const calculatePurchasesStats = (transformedPurchases) => {
  if (!transformedPurchases || !Array.isArray(transformedPurchases)) {
    return {
      totalPurchases: 0,
      totalAmount: 0,
      averageOrderValue: 0,
      orderedPurchases: 0,
      receivedPurchases: 0,
      pendingPurchases: 0,
      cancelledPurchases: 0,
      totalItems: 0
    };
  }

  const totalAmount = transformedPurchases.reduce((sum, purchase) => sum + purchase.rawAmount, 0);
  const orderedPurchases = transformedPurchases.filter(purchase => purchase.status === 'ordered').length;
  const receivedPurchases = transformedPurchases.filter(purchase => purchase.status === 'received').length;
  const pendingPurchases = transformedPurchases.filter(purchase => purchase.status === 'pending').length;
  const cancelledPurchases = transformedPurchases.filter(purchase => purchase.status === 'cancelled').length;
  const totalItems = transformedPurchases.reduce((sum, purchase) => sum + (purchase.items?.length || 0), 0);

  return {
    totalPurchases: transformedPurchases.length,
    totalAmount,
    averageOrderValue: transformedPurchases.length > 0 ? totalAmount / transformedPurchases.length : 0,
    orderedPurchases,
    receivedPurchases,
    pendingPurchases,
    cancelledPurchases,
    totalItems
  };
};

/**
 * Group purchases by period for charts
 */
export const groupPurchasesByPeriod = (transformedPurchases, periodType = 'month') => {
  if (!transformedPurchases || !Array.isArray(transformedPurchases)) return [];

  const periods = {};
  
  transformedPurchases.forEach(purchase => {
    const date = new Date(purchase.rawDate || Date.now());
    let periodKey;
    
    switch (periodType) {
      case 'day':
        periodKey = date.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        periodKey = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      case 'quarter':
        periodKey = `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
        break;
      case 'semester':
        periodKey = `${date.getFullYear()}-S${date.getMonth() < 6 ? 1 : 2}`;
        break;
      case 'year':
        periodKey = String(date.getFullYear());
        break;
      default:
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    
    if (!periods[periodKey]) {
      periods[periodKey] = {
        period: periodKey,
        purchases: [],
        totalAmount: 0,
        totalPurchases: 0
      };
    }
    
    periods[periodKey].purchases.push(purchase);
    periods[periodKey].totalAmount += purchase.rawAmount;
    periods[periodKey].totalPurchases += 1;
  });

  return Object.values(periods).sort((a, b) => a.period.localeCompare(b.period));
};

/**
 * Group purchases by supplier
 */
export const groupPurchasesBySupplier = (transformedPurchases) => {
  if (!transformedPurchases || !Array.isArray(transformedPurchases)) return [];

  const suppliers = {};
  
  transformedPurchases.forEach(purchase => {
    const supplierName = purchase.supplierName || 'مورد غير معروف';
    
    if (!suppliers[supplierName]) {
      suppliers[supplierName] = {
        supplier: supplierName,
        purchases: [],
        totalAmount: 0,
        totalPurchases: 0,
        totalItems: 0
      };
    }
    
    suppliers[supplierName].purchases.push(purchase);
    suppliers[supplierName].totalAmount += purchase.rawAmount;
    suppliers[supplierName].totalPurchases += 1;
    suppliers[supplierName].totalItems += purchase.items?.length || 0;
  });

  return Object.values(suppliers)
    .sort((a, b) => b.totalAmount - a.totalAmount);
};

/**
 * Group purchases by product
 */
export const groupPurchasesByProduct = (transformedPurchases, products = []) => {
  if (!transformedPurchases || !Array.isArray(transformedPurchases)) return [];

  const productPurchases = {};
  
  transformedPurchases.forEach(purchase => {
    if (purchase.items && Array.isArray(purchase.items)) {
      purchase.items.forEach(item => {
        const productId = item.id || item.productId;
        const product = products.find(p => p.id === productId);
        const productName = item.name || product?.name || 'منتج غير معروف';
        
        if (!productPurchases[productName]) {
          productPurchases[productName] = {
            product: productName,
            category: product?.category || 'أخرى',
            quantityPurchased: 0,
            totalAmount: 0,
            averagePrice: 0,
            purchaseCount: 0
          };
        }
        
        const quantity = Number(item.quantity || 0);
        const price = Number(item.price || 0);
        const itemTotal = Number(item.total || (quantity * price));
        
        productPurchases[productName].quantityPurchased += quantity;
        productPurchases[productName].totalAmount += itemTotal;
        productPurchases[productName].purchaseCount += 1;
        productPurchases[productName].averagePrice = 
          productPurchases[productName].quantityPurchased > 0 
            ? productPurchases[productName].totalAmount / productPurchases[productName].quantityPurchased 
            : 0;
      });
    }
  });

  return Object.values(productPurchases)
    .sort((a, b) => b.totalAmount - a.totalAmount);
};
